import { useCases } from "@/content/use-cases";
import { audiences, capabilities, controls, workflow } from "@/content/home";
import { Badge } from "@/components/ui/Badge";
import { capabilityStatusTone } from "@/lib/status";
import { ButtonLink } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Container } from "@/components/ui/Container";
import { GlobalCTA } from "@/components/ui/CTA";
import { List } from "@/components/ui/List";
import { Section } from "@/components/ui/Section";

export default function HomePage() {
  return (
    <>
      <section className="relative overflow-hidden bg-surface">
        <div
          className="pointer-events-none absolute -right-24 -top-24 size-80 rounded-full bg-mint/20"
          aria-hidden="true"
        />
        <Container className="relative grid gap-14 py-20 md:py-28 lg:grid-cols-[1.05fr_.95fr]">
          <div>
            <p className="inline-flex items-center rounded-full bg-brand-100 px-4 py-2 text-sm font-bold text-brand-900">
              Evidence-led creative growth
            </p>
            <h1 className="mt-6 text-4xl font-bold md:text-6xl">
              Evidence-led creative growth for brands and agencies
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-soft">
              Agentic Plus turns product facts, brand knowledge, market evidence, and growth goals
              into organized creative missions. It produces briefs, images, and standard video while
              keeping human approval over brand decisions, generation spend, and external
              advertising actions.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/contact" variant="primary">
                Request a demo
              </ButtonLink>
              <ButtonLink to="/how-it-works" variant="secondary">
                See how it works
              </ButtonLink>
            </div>
            <p className="mt-4 text-sm text-ink-soft">
              High-cost generation and external advertising actions require explicit approval.
            </p>
          </div>

          <div className="surface-card p-7">
            <h2 className="text-2xl font-bold">The growth mission loop</h2>
            <ol className="mt-6 space-y-4">
              {workflow.map((step, index) => (
                <li key={step.title} className="flex items-center gap-4">
                  <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-900 text-lg font-bold text-mint">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-semibold">{step.title}</p>
                    <p className="text-sm text-ink-soft">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      <Section
        title="Capabilities across one mission context"
        lead="Agentic Plus is organized around the complete path from evidence to approved creative and repeatable learning. Pilot and Planned capabilities are labeled explicitly."
        tone="paper"
      >
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.map((capability) => (
            <Card
              key={capability.title}
              title={capability.title}
              description={capability.description}
              footer={
                <Badge tone={capabilityStatusTone(capability.status)}>{capability.status}</Badge>
              }
            />
          ))}
        </div>
      </Section>

      <Section
        title="Who Agentic Plus is for"
        lead="The same mission loop supports brand teams, agencies, and buyers with different review needs."
        tone="surface"
      >
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {audiences.map((audience) => (
            <Card key={audience.title} title={audience.title}>
              <List items={audience.points} className="mt-4" />
            </Card>
          ))}
        </div>
      </Section>

      <Section
        title="Controls built into the workflow"
        lead="Agentic Plus keeps people accountable for brand, budget, and external actions."
      >
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {controls.map((control) => (
            <Card key={control.title} title={control.title} description={control.description} />
          ))}
        </div>
      </Section>

      <Section
        title="What teams can do next"
        lead="Agentic Plus supports the marketing moments where evidence and production decisions compound fastest."
        tone="surface"
      >
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {useCases.slice(0, 3).map((useCase) => (
            <Card key={useCase.id} title={useCase.title} description={useCase.problem}>
              <ButtonLink to="/use-cases" variant="secondary" className="mt-5">
                Read the use case
              </ButtonLink>
            </Card>
          ))}
        </div>
      </Section>

      <GlobalCTA />
    </>
  );
}
